/**
 * Current Package Card (Epic 3 — Toewijzing met tijdspercentages en
 * tijdvakken, Story 3.2)
 *
 * Gedeeld presentatiecomponent voor "Actueel pakket" (UX-spec S3 laag 1) —
 * hergebruikt met `readOnly` voor de medewerker-eigen "Mijn taken"-pagina
 * (UX-spec S8), zodat er geen twee losse implementaties ontstaan
 * (expliciete anti-duplicatie-richtlijn uit de story Dev Notes).
 *
 * Dit project heeft geen shadcn/ui geïnstalleerd (zie `CoreTasksTab.tsx`
 * Dev Notes) — Tooltip is `HoverTooltip` (custom, `components/`).
 * Kleuren uitsluitend XXtract-palet (navy #2F5A7A, teal #54949E, groen
 * #B7D945, rood alleen echte fouten — hier niet van toepassing, dit
 * component toont geen foutstaten).
 */

import { HoverTooltip } from '../../../components/HoverTooltip';
import { formatDutchDate } from '../../../lib/dateUtils';
import type { AssignmentPeriodDTO } from '@functieloonhuis/shared';

interface CurrentPackageCardProps {
  currentPackage: AssignmentPeriodDTO | null;
  isLoading: boolean;
  /** Medewerker-variant (S8): geen wijzig-knop, geen tijdlijn-beheer. */
  readOnly: boolean;
  /** Alleen relevant als !readOnly — opent de pakket-wijzigen-dialoog. */
  onChangePackage?: () => void;
  /**
   * Vaardigheden (ankers) per kerntaak-id, alleen gebruikt in de readOnly-
   * variant (S8: "uitklapbare vaardigheden"). De periode-response bevat
   * zelf geen vaardigheden (alleen naam/domein/percentage) — de aanroeper
   * kruist dit aan via de bestaande kerntakencatalogus (`useCoreTasks`).
   */
  skillsByCoreTaskId?: Record<number, string[]>;
}

export function CurrentPackageCard({
  currentPackage,
  isLoading,
  readOnly,
  onChangePackage,
  skillsByCoreTaskId,
}: CurrentPackageCardProps) {
  if (isLoading) {
    return (
      <div data-testid="current-package-card-loading" className="bg-white border border-[#E2E8F0] rounded-lg p-6">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2F5A7A]" />
          <span className="ml-2 text-sm text-gray-500">Laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="current-package-card" className="bg-white border border-[#E2E8F0] rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium text-gray-900">Actueel pakket</h2>
          {currentPackage && (
            <span
              data-testid="current-package-period-chip"
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
            >
              sinds {formatDutchDate(currentPackage.startDate)}
            </span>
          )}
        </div>
        {!readOnly && currentPackage && onChangePackage && (
          <button
            type="button"
            data-testid="change-package-button"
            onClick={onChangePackage}
            className="inline-flex items-center px-3 py-1.5 border border-[#54949E] text-xs font-medium rounded-md text-[#54949E] bg-white hover:bg-[#54949E]/10"
          >
            Pakket wijzigen
          </button>
        )}
      </div>

      {!currentPackage ? (
        <div data-testid="current-package-empty-state" className="bg-gray-50 rounded-lg p-6 text-center">
          <p className="text-sm font-medium text-gray-900">Geen actief pakket</p>
          <p className="mt-1 text-sm text-gray-500">
            Deze medewerker heeft nog geen kerntakenpakket. Stel een pakket samen om te starten.
          </p>
          {!readOnly && onChangePackage && (
            <button
              type="button"
              data-testid="compose-package-button"
              onClick={onChangePackage}
              className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[#2F5A7A] hover:bg-[#2F5A7A]/90"
            >
              Pakket samenstellen
            </button>
          )}
        </div>
      ) : (
        <>
          <ul className="space-y-3" data-testid="current-package-assignment-list">
            {currentPackage.assignments.map((assignment) => (
              <li key={assignment.coreTaskId} data-testid={`assignment-row-${assignment.coreTaskId}`}>
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{assignment.coreTaskName}</p>
                    <p className="text-xs text-gray-500 truncate">{assignment.domain}</p>
                  </div>
                  <div className="w-40 shrink-0">
                    <div className="h-6 rounded-md bg-gray-100 overflow-hidden relative">
                      <div
                        className="h-full bg-[#2F5A7A] flex items-center justify-end px-2"
                        style={{ width: `${assignment.timePercentage}%` }}
                      >
                        <span className="text-xs font-medium text-white">{assignment.timePercentage}%</span>
                      </div>
                    </div>
                  </div>
                </div>
                {readOnly && skillsByCoreTaskId?.[assignment.coreTaskId] && (
                  <details className="mt-1 ml-0" data-testid={`assignment-skills-${assignment.coreTaskId}`}>
                    <summary className="text-xs text-[#54949E] cursor-pointer">Vaardigheden</summary>
                    <ul className="mt-1 ml-4 list-disc text-xs text-gray-600 space-y-0.5">
                      {skillsByCoreTaskId[assignment.coreTaskId]!.map((skill) => (
                        <li key={skill}>{skill}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>

          {currentPackage.assignments.length > 3 && (
            <div className="mt-3">
              <HoverTooltip
                testId="exception-motivation-tooltip"
                content={currentPackage.exceptionMotivation || 'Geen motivatie opgegeven'}
              >
                <span
                  data-testid="exception-chip"
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#54949E]/10 text-[#54949E] cursor-default"
                >
                  Uitzondering
                </span>
              </HoverTooltip>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-[#E2E8F0] flex items-center gap-2 text-sm text-gray-500">
            <span data-testid="package-total-checkmark" className="text-[#B7D945]" aria-hidden="true">
              &#10003;
            </span>
            <span>Totaal 100%</span>
          </div>
        </>
      )}
    </div>
  );
}
